import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../../../api';
import Topbar from '../../Tools/Topbar';
import LeftSidebar from '../../Tools/LeftSidebar';
import Settings from '../../Tools/Setting/Settings';
import useSettings from '../../Tools/Setting/useSettings';
import StatusModal from './StatusModal/StatusModal';
import PatientNewCaseModal from '../EMSCasesTab/CaseDetails/PatientSituationModal';
import PatientSituationtransfercaseModal from '../TransferCasesTab/TransferCaseModal/PatientSituationtransfercase';
import { format } from 'date-fns';

// Date/Time Format Fetcher
const DateTimeFormatFetcher = () => {
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axiosInstance.get('/ems/get-system-setting', {
          headers: { Authorization: `Bearer ${sessionStorage.getItem('authToken')}` },
        });
        if (res.data?.status === 'success' && res.data.data.length > 0) {
          const { date_format = 'DD/MM/YYYY', time_format = '24' } = res.data.data[0];
          sessionStorage.setItem('date_format', date_format);
          sessionStorage.setItem('time_format', time_format);
        }
      } catch (err) {
        sessionStorage.setItem('date_format', 'DD/MM/YYYY');
        sessionStorage.setItem('time_format', '24');
      }
    };
    fetchSettings();
  }, []);
  return null;
};

const RequestsDetailTab = () => {
  const navigate = useNavigate();

  const [caseId] = useState(sessionStorage.getItem('currentCaseId'));
  const userId = sessionStorage.getItem('user_id');
  const caseStatus = sessionStorage.getItem('RequestTabCaseStatus');
  const [caseData, setCaseData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAcknowledging, setIsAcknowledging] = useState(false);
  const [showAcknowledgeButton, setShowAcknowledgeButton] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [nextStatus, setNextStatus] = useState(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isPatientModalOpen, setIsPatientModalOpen] = useState(false);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [isLoadingPatientSituation, setIsLoadingPatientSituation] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [userRole] = useState(sessionStorage.getItem('Role_Name') || '');

  const timerRef = useRef(null);
  const pollRef = useRef(null);
  const settingsRef = useRef();
  const patientModalRef = useRef();
  const transferModalRef = useRef();
  const { settings, updateSetting, handleColorTheme } = useSettings();

  const toggleSidebar = () => setIsSidebarOpen(prev => !prev);

  // Format date safely in UTC
 const formatCaseDate = (dateString) => {
   if (!dateString) return 'N/A';
 
   try {
     const dateFormat = sessionStorage.getItem('date_format') || 'DD/MM/YYYY';
     const timeFormat = sessionStorage.getItem('time_format') || '24';
 
     // Map your backend formats to date-fns
     const dateFormatMap = {
       'DD/MM/YYYY': 'dd/MM/yyyy',
       'MM/DD/YYYY': 'MM/dd/yyyy',
       'YYYY-MM-DD': 'yyyy-MM-dd',
     };
 
     const timeFormatString = timeFormat === '12' ? 'hh:mm:ss a' : 'HH:mm:ss';
     const formatString = `${dateFormatMap[dateFormat] || 'dd/MM/yyyy'} ${timeFormatString}`;
 
     let utcDate;
 
     if (dateString.includes('T')) {
       // ISO format: 2025-10-27T16:27:19.000000Z
       utcDate = new Date(dateString.endsWith('Z') ? dateString : dateString + 'Z');
     } else {
       // MySQL format: 2025-10-27 16:27:19
       utcDate = new Date(dateString.replace(' ', 'T') + 'Z'); // Force UTC
     }
 
     // Extract UTC parts manually to avoid local timezone shift
     const formatted = format(
       new Date(
         utcDate.getUTCFullYear(),
         utcDate.getUTCMonth(),
         utcDate.getUTCDate(),
         utcDate.getUTCHours(),
         utcDate.getUTCMinutes(),
         utcDate.getUTCSeconds()
       ),
       formatString
     );
 
     return formatted;
   } catch (err) {
     console.error('Error formatting alarm time:', err, dateString);
     return 'Invalid Date';
   }
 };

  // Fetch case details
  const fetchCaseDetails = async () => {
    if (!caseId) {
      setError('No case selected');
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      const res = await axiosInstance.get(`/ems/cases/${caseId}`);
      setCaseData(res.data.data || res.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load case');
    } finally {
      setIsLoading(false);
    }
  };

  // Check if a new status is available every 10 seconds
  const checkNextStatus = async () => {
    if (isUpdatingStatus) return;
    try {
      const response = await axiosInstance.get(`/ems/cases/${caseId}/next-status`);
      if (response.data?.next_status) {
        setNextStatus(response.data.next_status);
        setShowStatusModal(true);
      } else {
        scheduleNextCheck();
      }
    } catch (error) {
      console.error('Error checking next status:', error);
      scheduleNextCheck();
    }
  };

  const scheduleNextCheck = () => {
    timerRef.current = setTimeout(checkNextStatus, 10000); // Poll every 10 seconds
  };

  // Confirm and update case status
  const updateStatus = async () => {
    try {
      setIsUpdatingStatus(true);
      const response = await axiosInstance.post(`/ems/cases/${caseId}/update-status`);
      if (response.status === 200) {
        setShowStatusModal(false);
        setNextStatus(null);
        scheduleNextCheck();
      }
    } catch (error) {
      console.error('Error updating status:', error.response?.data || error.message);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleYesClick = () => updateStatus();
  const handleNoClick = () => {
    setShowStatusModal(false);
    setNextStatus(null);
    scheduleNextCheck();
  };

  // Start polling on mount
  useEffect(() => {
    scheduleNextCheck();
    return () => clearTimeout(timerRef.current);
  }, []);

  // Acknowledge button check
  const checkAcknowledgeButton = async () => {
    if (!caseId || !userId) return;
    try {
      const res = await axiosInstance.get(`/ems/show-case-acknowledgement/${caseId}/${userId}`);
      setShowAcknowledgeButton(res.data.show_button === true);
    } catch (err) {
      setShowAcknowledgeButton(false);
    }
  };

  // Acknowledge case
  const handleAcknowledge = async () => {
    try {
      setIsAcknowledging(true);
      setIsRefreshing(true);
      await axiosInstance.get(`/ems/case-acknowledgement/${caseId}`);
      alert('Case acknowledged successfully');
      await fetchCaseDetails();
      setShowAcknowledgeButton(false);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to acknowledge');
    } finally {
      setIsAcknowledging(false);
      setIsRefreshing(false);
    }
  };

  // Patient Situation Handlers
  const handleViewPatientSituation = () => setIsPatientModalOpen(true);

  const handleViewTransferCaseSituation = async () => {
    try {
      setIsLoadingPatientSituation(true);
      const res = await axiosInstance.get(`/ems/get-transfer-case-patient-situation/${caseId}`);
      sessionStorage.setItem('patientSituationData', JSON.stringify(res.data));
      setIsTransferModalOpen(true);
    } catch (err) {
      alert('Failed to load patient situation');
    } finally {
      setIsLoadingPatientSituation(false);
    }
  };

  const handlePatientSituationSave = async () => {
    setIsRefreshing(true);
    await fetchCaseDetails();
    setIsRefreshing(false);
  };

  // Poll for acknowledge button
  useEffect(() => {
    checkAcknowledgeButton();
    pollRef.current = setInterval(checkAcknowledgeButton, 5000);
    return () => clearInterval(pollRef.current);
  }, [caseId, userId]);

  // Initial load
  useEffect(() => {
    fetchCaseDetails();
  }, [caseId]);

  // Document handling
  const getDocuments = () => {
    if (!caseData) return [];
    return [caseData.documents1, caseData.documents2, caseData.documents3]
      .filter(doc => doc && doc.trim() !== '');
  };

  const handleDocumentClick = (url) => {
    setSelectedDocument(url);
    setShowDocumentModal(true);
  };

  const renderDocumentContent = (url) => {
    const ext = url.split('.').pop().toLowerCase();
    if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) {
      return <img src={url} alt="Doc" className="img-fluid" style={{ maxHeight: '80vh' }} />;
    }
    if (['pdf', 'xml'].includes(ext)) {
      return <iframe src={url} width="100%" height="600px" title="Document" />;
    }
    return <p>Unsupported format</p>;
  };

  const documents = getDocuments();

  return (
    <div id="main-wrapper">
      <DateTimeFormatFetcher />

      {/* Always render layout — this fixes hamburger menu */}
      <LeftSidebar isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
      <div className={`page-wrapper ${isSidebarOpen ? '' : 'sidebar-closed'}`}>
        <Topbar toggleSidebar={toggleSidebar} />

        <div className="body-wrapper">
          <div className="container-fluid">
            <Settings ref={settingsRef} updateSetting={updateSetting} handleColorTheme={handleColorTheme} settings={settings} />

            {/* Full-page refreshing spinner */}
            {isRefreshing && (
              <div className="d-flex justify-content-center align-items-center py-5 my-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Updating...</span>
                </div>
              </div>
            )}

            {/* Content loading spinner (inside layout) */}
            {isLoading && !isRefreshing && (
              <div className="d-flex justify-content-center align-items-center py-5 my-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading case...</span>
                </div>
              </div>
            )}

            {error && !isLoading && (
              <div className="alert alert-danger m-4">{error}</div>
            )}

            {!isLoading && !error && !caseData && (
              <div className="alert alert-warning m-4">No case data found</div>
            )}

            {!isLoading && !error && caseData && (
              <div className="card w-100 position-relative overflow-hidden">
                <div className="px-4 py-3 border-bottom">
                  <h5 className="card-title fw-semibold mb-0">Case Detail</h5>
                  <p className="card-subtitle mb-0 mt-1">Comprehensive case overview</p>
                </div>

                <div className="card-body p-4">
                  <div className="table-responsive">
                    <table className="table text-nowrap mb-0 align-middle">
                      <thead>
                        <tr>
                          <th><strong>Field</strong></th>
                          <th><strong>Value</strong></th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* Case Information Rows */}
                        <tr>
                          <td><p className="mb-0 fw-normal fw-semibold">Case ID</p></td>
                          <td><p className="mb-0 fw-normal">{caseData.id}</p></td>
                        </tr>
                        <tr>
                          <td><p className="mb-0 fw-normal fw-semibold">Name</p></td>
                          <td><p className="mb-0 fw-normal">{caseData.case_name}</p></td>
                        </tr>
                        <tr>
                          <td><p className="mb-0 fw-normal fw-semibold">Location</p></td>
                          <td><p className="mb-0 fw-normal">{caseData.location?.location_name || 'N/A'}</p></td>
                        </tr>
                        <tr>
                          <td><p className="mb-0 fw-normal fw-semibold">Ambulance</p></td>
                          <td><p className="mb-0 fw-normal">{caseData.ambulance?.plate_number || 'N/A'}</p></td>
                        </tr>
                        <tr>
                          <td><p className="mb-0 fw-normal fw-semibold">Transfer To</p></td>
                          <td><p className="mb-0 fw-normal">{caseData.medical_center?.name || 'N/A'}</p></td>
                        </tr>
                        <tr>
                          <td><p className="mb-0 fw-normal fw-semibold">Paramedics ID</p></td>
                          <td><p className="mb-0 fw-normal">{caseData.ems_member || 'N/A'}</p></td>
                        </tr>
                        <tr>
                          <td><p className="mb-0 fw-normal fw-semibold">Paramedics Name</p></td>
                          <td><p className="mb-0 fw-normal">{caseData.ems_member_name || 'N/A'}</p></td>
                        </tr>
                        <tr>
                          <td><p className="mb-0 fw-normal fw-semibold">Paramedics Status</p></td>
                          <td><p className="mb-0 fw-normal">{caseData.ems_member?.status ? 'Busy' : 'Available'}</p></td>
                        </tr>

                        {caseStatus === '1' && (
                          <>
                            <tr>
                              <td><p className="mb-0 fw-normal fw-semibold mb-0">Patient Location</p></td>
                              <td><p className="mb-0 fw-normal">
                                {caseData?.p_location?.location_name || 'N/A'}
                              </p></td>
                            </tr>
                            <tr>
                              <td><p className="mb-0 fw-normal fw-semibold mb-0">Patient Area</p></td>
                              <td><p className="mb-0 fw-normal">
                                {caseData?.p_area?.area_name || 'N/A'}
                              </p></td>
                            </tr>
                          </>
                        )}

                        {/* Patient Details */}
                        <tr>
                          <td><p className="mb-0 fw-normal fw-semibold">Accident Place</p></td>
                          <td><p className="mb-0 fw-normal">{caseData.accident_place || 'N/A'}</p></td>
                        </tr>
                        <tr>
                          <td><p className="mb-0 fw-normal fw-semibold">Gender</p></td>
                          <td><p className="mb-0 fw-normal">{caseData.gender || 'N/A'}</p></td>
                        </tr>
                        <tr>
                          <td><p className="mb-0 fw-normal fw-semibold">Age</p></td>
                          <td><p className="mb-0 fw-normal">{caseData.age || 'N/A'}</p></td>
                        </tr>
                        <tr>
                          <td><p className="mb-0 fw-normal fw-semibold">Bracelet ID</p></td>
                          <td><p className="mb-0 fw-normal">{caseData.bracelet_id || 'N/A'}</p></td>
                        </tr>
                        <tr>
                          <td><p className="mb-0 fw-normal fw-semibold">Type of Injury</p></td>
                          <td><p className="mb-0 fw-normal">{caseData.injury_type || 'N/A'}</p></td>
                        </tr>
                        <tr>
                          <td><p className="mb-0 fw-normal fw-semibold">Contact Number</p></td>
                          <td><p className="mb-0 fw-normal">{caseData.contact_number || 'N/A'}</p></td>
                        </tr>
                        <tr>
                          <td><p className="mb-0 fw-normal fw-semibold">Triage Card</p></td>
                          <td><p className="mb-0 fw-normal">{caseData.triage_card || 'N/A'}</p></td>
                        </tr>
                        <tr>
                          <td><p className="mb-0 fw-normal fw-semibold">Acknowledgment</p></td>
                          <td><p className="mb-0 fw-normal">{caseData.acknowledgment || 'Pending'}</p></td>
                        </tr>

                        {/* Documents */}
                        <tr>
                          <td><p className="mb-0 fw-normal fw-semibold">Documents</p></td>
                          <td>
                            <p className="mb-0 fw-normal">
                              <i className="ti ti-paperclip me-2"></i>
                              {documents.length > 0 ? (
                                documents.map((doc, index) => (
                                  <button
                                    key={index}
                                    type="button"
                                    className="btn btn-link me-3 p-0"
                                    onClick={() => handleDocumentClick(doc)}
                                    title={`Document ${index + 1}`}
                                  >
                                    <i className="ti ti-library-photo"></i>
                                  </button>
                                ))
                              ) : (
                                'No documents'
                              )}
                            </p>
                          </td>
                        </tr>

                        {/* Case Creation Date */}
                        <tr>
                          <td><p className="mb-0 fw-normal fw-semibold">Case Date</p></td>
                          <td><p className="mb-0 fw-normal">{caseData.created_at ? formatCaseDate(caseData.created_at) : 'N/A'}</p></td>
                        </tr>

                        {/* Patient Situation Link (New Case) */}
                        {caseStatus === '1' && (
                          <tr>
                            <td><p className="mb-0 fw-normal fw-semibold">Patient Situation</p></td>
                            <td>
                              <p className="mb-0 fw-normal">
                                <a
                                  data-bs-toggle="modal"
                                  data-bs-target="#bs-fullscreen"
                                  className="text-primary"
                                  onClick={handleViewPatientSituation}
                                >
                                  Click to view details
                                </a>
                              </p>
                            </td>
                          </tr>
                        )}

                        {/* Patient Situation Link (Transfer Case) */}
                        {caseStatus === '2' && (
                          <tr>
                            <td><p className="mb-0 fw-normal fw-semibold">Patient Situation</p></td>
                            <td>
                              <p className="mb-0 fw-normal">
                                {isLoadingPatientSituation ? (
                                  <span className="text-muted">Loading...</span>
                                ) : (
                                  <a
                                    className="text-primary"
                                    onClick={handleViewTransferCaseSituation}
                                    style={{ cursor: 'pointer' }}
                                  >
                                    Click to view details
                                  </a>
                                )}
                              </p>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>

                    {/* Acknowledge Button */}
                    {showAcknowledgeButton && (userRole === 'EMS Team' || userRole === 'Hospital Representative (Nurse)') && (
                      <div className="mt-4">
                        <button
                          className="btn btn-success rounded-pill px-4"
                          onClick={handleAcknowledge}
                          disabled={isAcknowledging}
                        >
                          {isAcknowledging ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2"></span>
                              Acknowledging...
                            </>
                          ) : 'Acknowledge'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Document Modal */}
            {showDocumentModal && (
              <div className="modal fade show d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
                <div className="modal-dialog modal-lg">
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5 className="modal-title">Attachment</h5>
                      <button type="button" className="btn-close" onClick={() => setShowDocumentModal(false)}></button>
                    </div>
                    <div className="modal-body text-center">
                      {selectedDocument && renderDocumentContent(selectedDocument)}
                    </div>
                    <div className="modal-footer">
                      <button className="btn btn-danger" onClick={() => setShowDocumentModal(false)}>Close</button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Status Modal */}
            {userRole === 'EMS Team' && (
              <StatusModal 
              show={showStatusModal} 
              nextStatus={nextStatus} 
              onYesClick={handleYesClick} 
              onNoClick={handleNoClick} />
            )}

            {/* Patient Modals */}
            <PatientNewCaseModal
              ref={patientModalRef}
              isOpen={isPatientModalOpen}
              onClose={() => setIsPatientModalOpen(false)}
              onSave={handlePatientSituationSave}
              caseId={caseId}
            />

            <PatientSituationtransfercaseModal
              ref={transferModalRef}
              isOpen={isTransferModalOpen}
              onClose={() => setIsTransferModalOpen(false)}
              onSave={handlePatientSituationSave}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestsDetailTab;